import { Dispatch, SetStateAction } from "react";

import classNames from "classnames/bind";
import { NextPrevButton } from "common/blocks/NextPrevButton";
import UIIcon from "common/blocks/UIIcon";
import useMediaQuery from "hooks/useMediaQuery/useMediaQuery";
import { DEFAULT_IMAGE_URL } from "lib/constants";
import { VehicleService } from "services/vehicle/vehicle";
import { FreeMode, Keyboard, Navigation, Thumbs } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import styles from "./VehicleImageCarousel.module.scss";

const cx = classNames.bind(styles);

export type VehicleImageCarouselProps = {
    media: VehicleService.Imagery;
    currentSelectedImageIndex: number;
    setCurrentSelectedImageIndex: Dispatch<SetStateAction<number>>;
    showPromo?: boolean;
    handleImageViewerVideoOpen: () => void;
    handleImageViewerImageOpen: (activeIndex: number) => void;
    swiperRef: React.MutableRefObject<any>;
};

const VehicleImageCarousel = ({
    media,
    currentSelectedImageIndex,
    setCurrentSelectedImageIndex,
    handleImageViewerImageOpen,
    handleImageViewerVideoOpen,
    swiperRef,
}: VehicleImageCarouselProps) => {
    const { isMobile, isTablet } = useMediaQuery();

    const totalNumberOfImages = media.MarketingImages?.length + media.OperationalImages?.length;
    const slideOffset = isTablet ? 0 : isMobile ? 10 : 0;
    const numberOfSlidesVisible = isTablet ? 4.5 : isMobile ? "auto" : 4.9924;
    const fallbackImage = DEFAULT_IMAGE_URL;
    const combinedImages = [...(media?.MarketingImages || []), ...(media?.OperationalImages || [])];

    const getPictureElementFallbackImage = (image: VehicleService.MarketingImage) => {
        let xsImg = image?.SizedImages?.find((a) => a.Size == "ExtraSmall")?.Uri;
        let sImg = image?.SizedImages?.find((i) => i.Size == "Small")?.Uri;
        if (xsImg && xsImg?.length > 0) {
            return xsImg;
        } else if (sImg && sImg?.length > 0) {
            return sImg;
        } else {
            return image.Uri;
        }
    };

    return !!combinedImages?.length ? (
        <>
            <div id="vehicle-image-carousel" className={cx("vehicleImageCarousel")}>
                <nav className={cx("navigationLeft")}>
                    <NextPrevButton
                        id="vehicleImagePrevButton"
                        direction="Left"
                        ariaLabelText="Previous image"
                    />
                </nav>
                <nav className={cx("navigationRight")}>
                    <NextPrevButton
                        id="vehicleImageNextButton"
                        direction="Right"
                        ariaLabelText="Next image"
                    />
                </nav>
                <Swiper
                    onSwiper={(swiper: any) => {
                        swiperRef.current = swiper;
                    }}
                    mousewheel={true}
                    keyboard={true}
                    freeMode={true}
                    watchSlidesProgress={true}
                    modules={[FreeMode, Navigation, Thumbs, Keyboard]}
                    spaceBetween={10}
                    slidesOffsetBefore={slideOffset}
                    loop
                    slidesPerView={numberOfSlidesVisible}
                    navigation={{
                        nextEl: "#vehicleImageNextButton",
                        prevEl: "#vehicleImagePrevButton",
                    }}
                >
                    {combinedImages?.length ? (
                        combinedImages.map((image, index) => {
                            let pictureFallbackImage = getPictureElementFallbackImage(image);
                            return (
                                <SwiperSlide
                                    key={`vehicle-carousel-${image.Angle}`}
                                    data-key={`vehicle-carousel-${image.Angle}`}
                                    onClick={() => setCurrentSelectedImageIndex(index)}
                                >
                                    {image?.SourceId == 3 ? (
                                        <picture
                                            className={cx("swiper-picture")}
                                            key={`picture-${image.Angle}-${index}`}
                                            data-key={`picture-${image.Angle}-${index}`}
                                        >
                                            {image?.SizedImages.map((i, index) => {
                                                let mediaQueryString: string;
                                                switch (i.Size) {
                                                    case "ExtraSmall":
                                                        mediaQueryString = "(min-width: 0px)";
                                                        break;
                                                    default:
                                                        return;
                                                }
                                                return (
                                                    <>
                                                        <source
                                                            key={index}
                                                            media={mediaQueryString}
                                                            srcSet={i.Uri}
                                                            type={`image/${i.Format}`}
                                                            datatype={`image/${i.Format}`}
                                                        />
                                                    </>
                                                );
                                            })}
                                            <img
                                                src={pictureFallbackImage}
                                                alt={image.Description}
                                                width={244}
                                                height={183}
                                                style={{ objectFit: "contain", height: "auto" }}
                                                loading={index <= 5 ? "eager" : "lazy"}
                                                fetchPriority={index <= 5 ? "auto" : "low"}
                                            />
                                        </picture>
                                    ) : (
                                        <>
                                            {/* eslint-disable-next-line @next/next/no-img-element */}
                                            <img
                                                src={pictureFallbackImage}
                                                alt={image.Description}
                                                width={244}
                                                height={183}
                                                style={{ objectFit: "contain", height: "auto" }}
                                                loading={index <= 5 ? "eager" : "lazy"}
                                                fetchPriority={index <= 5 ? "auto" : "low"}
                                            />
                                        </>
                                    )}
                                </SwiperSlide>
                            );
                        })
                    ) : (
                        <SwiperSlide>
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img
                                src={fallbackImage}
                                alt="You've found new stock, images coming soon!"
                                style={{ objectFit: "contain", height: "auto" }}
                                loading="eager"
                            />
                        </SwiperSlide>
                    )}
                </Swiper>
                <div className={cx("galleryVideoButtons")}>
                    <button
                        onClick={() => handleImageViewerImageOpen(currentSelectedImageIndex)}
                        className={cx("galleryVideoButton")}
                        aria-label="Open Gallery"
                    >
                        <UIIcon icon="GridInterface" />{" "}
                        <span className={cx("galleryVideoButtonCol")}>
                            <strong>Gallery</strong> {totalNumberOfImages} images
                        </span>
                    </button>
                    {!!media?.MarketingVideos?.length && (
                        <button
                            onClick={handleImageViewerVideoOpen}
                            className={cx("galleryVideoButton")}
                            aria-label="Open 360&deg;"
                        >
                            <UIIcon icon="Video" />{" "}
                            <span className={cx("galleryVideoButtonCol")}>
                                <strong>View 360&deg;</strong>
                            </span>
                        </button>
                    )}
                </div>
            </div>
        </>
    ) : null;
};

export default VehicleImageCarousel;
